import React from "react";
import { Controller } from 'react-hook-form';
import Calendar from "react-range-calendar";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FormControl, TextField, InputAdornment, IconButton, Box } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ErrorIcon from '@mui/icons-material/Error';
import { makeStyles } from "@material-ui/core/styles";
import dayjs from 'dayjs';

const useStyles = makeStyles({
  disabledDate: {
    color: '#d3d3d3', // Grey color for disabled dates
    pointerEvents: 'none', // Disable click events
  },
});

const CustomStaticDateRangePicker1 = ({
  control,
  trigger,
  name,
  label,
  error,
  helperText,
  handleBlur,
  minDate,
  maxDate,
  maxDays,
  onDateChange,
  setInvalidRange,
  invalidRange
}) => {
  const [open, setOpen] = React.useState(false);
  const classes = useStyles();
  const defaultStartDate = dayjs().startOf('month').toDate();
  const defaultEndDate = dayjs();

  const handleOpen = (event) => {
    event.stopPropagation(); // Prevent click propagation issues
    setOpen(prevOpen => !prevOpen);
  };
  const handleClose = () => setOpen(false);

  const parseDate = (date) => (date ? dayjs(date).startOf('day').toDate() : null);

  return (
    <FormControl fullWidth margin="normal" style={{ position: 'relative' }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <>
              <TextField
                label={label}
                onClick={handleOpen}
                variant="outlined"
                value={`${field.value && field.value[0] ? parseDate(field.value[0])?.toLocaleDateString() : defaultStartDate.toLocaleDateString()} - ${field.value && field.value[1] ? parseDate(field.value[1])?.toLocaleDateString() : defaultEndDate.toLocaleDateString()}`} 
               onBlur={(e) => {
                  if (handleBlur) {
                    handleBlur(e);
                  }
                }}
                helperText={invalidRange ? "Invalid date range selected." : helperText}
                error={!!error || invalidRange}
                InputProps={{
                  endAdornment: (
                    <>
                      <InputAdornment position="end">
                        <IconButton onClick={handleOpen}>
                          <CalendarTodayIcon />
                        </IconButton>
                      </InputAdornment>
                      {error && (
                        <InputAdornment position="relative">
                          <ErrorIcon style={{ color: 'red' }} />
                        </InputAdornment>
                      )}
                    </>
                  ),
                }}
              />
              {open && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1,
                    mt: 1,
                    boxShadow: 3,
                    borderRadius: 1,
                    backgroundColor: 'white',
                  }}
                >
                  <Calendar
                    visible={open}
                    dateRange={field.value ? [parseDate(field.value[0]), parseDate(field.value[1])] : [minDate, maxDate]}
                    onDateClick={(minDateSelected, maxDateSelected) => {
                      const start = dayjs(minDateSelected).startOf('day');
                      const end = dayjs(maxDateSelected).startOf('day');
                      const today = dayjs().startOf('day');
                      const minAllowedStart = today.subtract(12, 'month');  // 12 months ago
                      const isStartValid = !start.isBefore(minAllowedStart);
                      const isEndValid = !end.isBefore(start);
                      const isRangeValid = end.diff(start, 'day') <= 31;
                      
                      if (isStartValid && isEndValid && isRangeValid) {
                        field.onChange([minDateSelected, maxDateSelected]);
                        trigger(name);
                        onDateChange?.([minDateSelected, maxDateSelected]);
                        setInvalidRange(false);
                        handleClose();
                      } else {
                        setInvalidRange(true);
                      }
                    }}                    
                    type="free-range"
                    minDate={minDate || undefined} // Dynamic min date from parent
                    maxDate={maxDate || undefined} // Dynamic max date from parent
                    renderDay={(day, selectedDate, isInCurrentMonth, dayComponent) => {
                      const today = dayjs().startOf('day');
                      const oneYearAgo = today.subtract(12, 'month');
                      const isBeforeStartRange = dayjs(day).isBefore(oneYearAgo);
                      const isAfterStartRange = dayjs(day).isAfter(today);
                      const isDisabled = isBeforeStartRange || isAfterStartRange;

                      return React.cloneElement(dayComponent, {
                        className: isDisabled ? classes.disabledDate : '',
                        onClick: isDisabled ? undefined : dayComponent.props.onClick,
                      });
                    }}
                  />
                </Box>
              )}
            </>
          )}
        />
      </LocalizationProvider>
    </FormControl>
  );
};

export default CustomStaticDateRangePicker1;